/**
 * Agrégats de revenus. Fonctions pures — voir tests/revenue.test.js.
 *
 * Règle du modèle : revenu = effectif × tarif mensuel par utilisateur. Seuls
 * les clients payants produisent du revenu ; les partenaires sont gratuits
 * par définition et sont donc comptés en nombre, jamais en argent.
 *
 * Ces fonctions travaillent sur des ENTITÉS COMMERCIALES — GMF et départements
 * d'hôpital — et non sur des établissements. Un hôpital n'a pas de revenu
 * propre : il n'a que la somme de ses départements. Voir domain/entities.js.
 */

import { countPayments, paymentDayOf } from './billing.js'
import { usersOf } from './estimate.js'
import { recordKey } from './record.js'

const EMPTY = {
  monthly: 0, // revenu mensuel réel (clients payants)
  clientCount: 0,
  userCount: 0, // utilisateurs facturés
  partnerCount: 0,
  pipeline: 0, // revenu mensuel si les « en conversion » signaient
  prospectValue: 0, // idem pour les prospects — le marché total adressable
}

/**
 * @param {Array} entities  entités commerciales — voir commercialEntities()
 * @param {Map}   records   clé recordKey() -> dossier
 * @param {{tarif: number, ratioPro: number}} settings
 */
export function computeRevenue(entities, records, settings) {
  const tarif = Number.parseFloat(settings?.tarif) || 0

  return entities.reduce((acc, est) => {
    const record = records.get(recordKey(est.kind, est.id))
    const users = usersOf(est, record, settings?.ratioPro)
    const value = users * tarif
    const status = record?.status || 'prospect'

    switch (status) {
      case 'client':
        return {
          ...acc,
          monthly: acc.monthly + value,
          clientCount: acc.clientCount + 1,
          userCount: acc.userCount + users,
        }
      case 'gratuit':
        return { ...acc, partnerCount: acc.partnerCount + 1 }
      case 'conversion':
        return { ...acc, pipeline: acc.pipeline + value }
      default:
        return { ...acc, prospectValue: acc.prospectValue + value }
    }
  }, EMPTY)
}

/**
 * Répartition par statut, pour les compteurs de la barre latérale.
 *
 * Compte des entités commerciales, pas des établissements : « Clients : 5 »
 * peut valoir 3 GMF et 2 départements d'hôpital. C'est le nombre réel de gens
 * qui paient, et c'est la question à laquelle ce chiffre répond.
 */
export function countByStatus(entities, records) {
  const counts = { prospect: 0, conversion: 0, client: 0, gratuit: 0 }
  for (const est of entities) {
    const status = records.get(recordKey(est.kind, est.id))?.status || 'prospect'
    if (counts[status] !== undefined) counts[status] += 1
  }
  return counts
}

/**
 * Cumul facturé par toute la clientèle depuis ses débuts respectifs.
 *
 * Somme des cumuls individuels : un client sans date de début, ou sans
 * effectif connu, n'apporte rien plutôt que d'être approximé. Le total est donc
 * un plancher — ce qui est certain — et non une estimation optimiste.
 *
 * @param {Date} now  injecté pour rendre la fonction testable
 * @returns {{total: number, counted: number, skipped: number}}
 *   `counted` / `skipped` disent sur combien de clients le total repose.
 */
export function computeTotalEarned(entities, records, settings, now = new Date()) {
  const tarif = Number.parseFloat(settings?.tarif) || 0
  let total = 0
  let counted = 0
  let skipped = 0

  for (const est of entities) {
    const record = records.get(recordKey(est.kind, est.id))
    if (record?.status !== 'client') continue

    const users = usersOf(est, record, settings?.ratioPro)
    const earned = totalEarned(record, users, tarif, now)
    if (earned) {
      total += earned.total
      counted += 1
    } else {
      skipped += 1
    }
  }

  return { total, counted, skipped }
}

/**
 * Cumul facturé depuis le passage en client.
 *
 * Compte les versements réellement échus — un client paie à date fixe, une fois
 * par mois. Le calcul précédent multipliait par des mois fractionnaires et
 * annonçait l'équivalent de 5,95 versements ; aucune facture n'a jamais porté
 * ce montant.
 *
 * Approximation qui demeure : l'effectif et le tarif d'aujourd'hui sont
 * appliqués à tous les versements passés. Sans historique des changements,
 * c'est le mieux disponible ; le chiffre dérive donc si le tarif a bougé.
 *
 * @param {Date} now  injecté pour rendre la fonction testable
 * @returns {{total: number, payments: number, paymentDay: number} | null}
 */
export function totalEarned(record, users, tarif, now = new Date()) {
  if (!record || record.status !== 'client' || !record.since) return null
  if (!(tarif > 0) || !(users > 0)) return null

  const paymentDay = paymentDayOf(record)
  const payments = countPayments(record.since, paymentDay, now)
  if (payments === null) return null

  return { total: payments * users * tarif, payments, paymentDay }
}
